import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Label,
  Input,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import ActionButton from '../../../Components/Common/ActionButton';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi, proHydroAxios } from '../../../helpers/crude_api_helper';

// DMUT List — ProHydro: AplikasiDmutApisController (06.WebApi, `proHydro`
// client per api-mapping.md), per api-contract-dmut-list.md.
//   list   GET /v1/AplikasiDmutApis/DmutList       (paginated, per-PIC)
//   export GET /v1/AplikasiDmutApis/DmutListExcel  (same filters, ignores
//                                                    page/pageSize, all rows)
// Data is proxied live from the external eRS app on every request — no local
// cache, no create/update/delete (this menu is read-only + export).
// `sortField` whitelist (PascalCase): CounterPartyName, Country, Status,
// SubStatus, PicName, PicEmail. Default sort: CounterPartyName ascending.
// `productInterest`/`intention` must never be sent empty — the contract's BE
// validation 400s on an empty string (mirrors eRS's own requirement).
const LIST_ENDPOINT = '/v1/AplikasiDmutApis/DmutList';
const EXCEL_ENDPOINT = '/v1/AplikasiDmutApis/DmutListExcel';
const MAX_TEXT_LENGTH = 100;

const defaultFilters = {
  companyName: '',
  businessCategoryName: '',
  productInterest: 'Crude & Condensate',
  intention: 'Supplier',
  tenderInvitation: true,
  periode: 'Term',
};

const toPascalCase = field =>
  field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

const buildQuery = params => {
  const entries = Object.entries(params || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
  if (!entries.length) return '';
  return `?${entries
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&')}`;
};

const DmutListView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Draft filter inputs (what the user is typing) vs. applied filters (what
  // was last sent to the backend) — the Search button is an explicit trigger,
  // not live-as-you-type, per spec.
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [errors, setErrors] = useState({});

  document.title = 'Registered Supplier List';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await proHydroApi.get(LIST_ENDPOINT, {
        page,
        pageSize,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
        ...appliedFilters,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load Registered Supplier list: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortField, sortOrder, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setDraftFilters(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateFilters = () => {
    const newErrors = {};
    // Contract: productInterest/intention required, all 4 text fields capped
    // at 100 chars.
    if (!draftFilters.productInterest || !draftFilters.productInterest.trim()) {
      newErrors.productInterest = 'Product Interest is required';
    }
    if (!draftFilters.intention || !draftFilters.intention.trim()) {
      newErrors.intention = 'Intention is required';
    }
    [
      'companyName',
      'businessCategoryName',
      'productInterest',
      'intention',
    ].forEach(field => {
      if ((draftFilters[field] || '').length > MAX_TEXT_LENGTH) {
        newErrors[field] = `Max ${MAX_TEXT_LENGTH} characters`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = () => {
    if (!validateFilters()) return;
    setAppliedFilters(draftFilters);
    setPage(1);
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.id) return;
    setSortField(toPascalCase(column.id));
    setSortOrder(sortDirection === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  const handleExport = async () => {
    // Client-side pre-check: avoid hitting the export endpoint (which 404s
    // on an empty result) when the current filters already show 0 rows.
    if (totalCount === 0) {
      toast.error('Tidak ada data untuk diekspor');
      return;
    }
    setExporting(true);
    try {
      const blob = await proHydroAxios.get(
        `${EXCEL_ENDPOINT}${buildQuery({
          sortField: sortField || undefined,
          sortOrder: sortField ? sortOrder : undefined,
          ...appliedFilters,
        })}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Registered Supplier List.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to export data: ${error}`);
    } finally {
      setExporting(false);
    }
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      id: 'counterPartyName',
      name: headerLabel('Name of Counter Parties'),
      selector: row => row.counterPartyName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'country',
      name: headerLabel('Country'),
      selector: row => row.country,
      sortable: true,
      wrap: true,
    },
    {
      id: 'status',
      name: headerLabel('Status'),
      selector: row => row.status,
      sortable: true,
      wrap: true,
    },
    {
      id: 'subStatus',
      name: headerLabel('Sub Status'),
      selector: row => row.subStatus,
      sortable: true,
      wrap: true,
    },
    {
      id: 'picName',
      name: headerLabel('PIC Name'),
      selector: row => row.picName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'picEmail',
      name: headerLabel('PIC Email'),
      selector: row => row.picEmail,
      sortable: true,
      wrap: true,
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Registered Supplier List' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className='border-0'>
                  <h5 className='card-title mb-0'>Filter</h5>
                </CardHeader>
                <CardBody className='pt-0'>
                  <Row>
                    <Col md={4}>
                      <div className='mb-3'>
                        <Label for='companyName' className='form-label'>
                          Company Name
                        </Label>
                        <Input
                          type='text'
                          id='companyName'
                          name='companyName'
                          maxLength={MAX_TEXT_LENGTH}
                          invalid={!!errors.companyName}
                          value={draftFilters.companyName}
                          onChange={handleFilterChange}
                        />
                        {errors.companyName && (
                          <div className='invalid-feedback d-block'>
                            {errors.companyName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className='mb-3'>
                        <Label
                          for='businessCategoryName'
                          className='form-label'
                        >
                          Business Category Name
                        </Label>
                        <Input
                          type='text'
                          id='businessCategoryName'
                          name='businessCategoryName'
                          maxLength={MAX_TEXT_LENGTH}
                          invalid={!!errors.businessCategoryName}
                          value={draftFilters.businessCategoryName}
                          onChange={handleFilterChange}
                        />
                        {errors.businessCategoryName && (
                          <div className='invalid-feedback d-block'>
                            {errors.businessCategoryName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className='mb-3'>
                        <Label for='productInterest' className='form-label'>
                          Product Interest{' '}
                          <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='productInterest'
                          name='productInterest'
                          maxLength={MAX_TEXT_LENGTH}
                          invalid={!!errors.productInterest}
                          value={draftFilters.productInterest}
                          onChange={handleFilterChange}
                        />
                        {errors.productInterest && (
                          <div className='invalid-feedback d-block'>
                            {errors.productInterest}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className='mb-3'>
                        <Label for='intention' className='form-label'>
                          Intention <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='intention'
                          name='intention'
                          maxLength={MAX_TEXT_LENGTH}
                          invalid={!!errors.intention}
                          value={draftFilters.intention}
                          onChange={handleFilterChange}
                        />
                        {errors.intention && (
                          <div className='invalid-feedback d-block'>
                            {errors.intention}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className='mb-3'>
                        <Label for='tenderInvitation' className='form-label'>
                          Tender Invitation
                        </Label>
                        <Input
                          type='select'
                          id='tenderInvitation'
                          name='tenderInvitation'
                          value={String(draftFilters.tenderInvitation)}
                          onChange={e =>
                            setDraftFilters(prev => ({
                              ...prev,
                              tenderInvitation: e.target.value === 'true',
                            }))
                          }
                        >
                          <option value='true'>Yes</option>
                          <option value='false'>No</option>
                        </Input>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className='mb-3'>
                        <Label for='periode' className='form-label'>
                          Period
                        </Label>
                        <Input
                          type='select'
                          id='periode'
                          name='periode'
                          value={draftFilters.periode}
                          onChange={handleFilterChange}
                        >
                          <option value='Term'>Term</option>
                          <option value='Spot'>Spot</option>
                        </Input>
                      </div>
                    </Col>
                  </Row>
                  <div className='d-flex gap-2'>
                    <ActionButton
                      variant='primary'
                      size='sm'
                      icon='ri-search-line'
                      onClick={handleSearch}
                    >
                      Search
                    </ActionButton>
                    <ActionButton
                      variant='secondary'
                      size='sm'
                      icon='ri-download-2-line'
                      onClick={handleExport}
                      disabled={exporting}
                    >
                      {exporting ? 'Exporting...' : 'Export to Excel'}
                    </ActionButton>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card id='DmutList'>
                <CardHeader className='border-0'>
                  <h5 className='card-title mb-0'>Registered Supplier List</h5>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No Registered Supplier data found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DmutListView;
